import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthService } from '@services/core/auth.service';
import { Global } from '@services/core/Global';

@Injectable({
  providedIn: 'root'
})
export class ClaimService {
  public url: string;
  protected http = inject(HttpClient);
  private authService = inject(AuthService);

  constructor() {
    this.url = Global.url;
  }

  protected convertToFilter(objeto: any): HttpParams {
    let params = new HttpParams();
    const hasKeys = Object.keys(objeto ?? {})?.length > 0;
    if (hasKeys) {
      params = this.createKeys(objeto, params);
    }
    return params;
  }

  protected createKeys(objeto: any, params: HttpParams): HttpParams {
    for (const clave in objeto) {
      if (objeto[clave] != null && objeto[clave] != undefined) {
        params = params.append(clave, objeto[clave]);
      }
    }
    return params;
  }

  buildUrl(params: any): string {
    let queryParams = '';
    if (params) {
      queryParams = Object.keys(params)
        .map((key) => `${params[key]}`)
        .join('/');
    }
    return queryParams ? `${queryParams}` : '';
  }

  getProcessDocumentRefView(params: any): Observable<any> {
    const httpParams = this.convertToFilter(params);
    return this.http.get(this.url + 'V1/process-document-ref-view', { params: httpParams });
  }

  getProcesses(params: any): Observable<any> {
    const httpParams = this.convertToFilter(params);
    return this.http.get(this.url + 'V1/process-documents', { params: httpParams });
  }

  getPeriods(): Observable<any> {
    return this.http.get(this.url + 'V1/periods');
  }

  getFdrAccounts(): Observable<any> {
    return this.http.get(this.url + 'V1/fdr-accounts/');
  }

  updateFdrAccount(params: any): Observable<any> {
    return this.http.put(this.url + 'V1/fdr-accounts/' + params.id, params);
  }

  saveFdrAccountMovements(params: any): Observable<any> {
    return this.http.post(this.url + 'V1/fdr-account-movements/', params);
  }

  getStatus(): Observable<any> {
    return this.http.get(this.url + 'V1/status');
  }

  getEntities(): Observable<any> {
    return this.http.get(this.url + 'V1/fpv-entities');
  }

  getParties(params?: any): Observable<any> {
    const httpParams = this.convertToFilter(params);
    return this.http.get(this.url + 'V1/parties-view', { params: httpParams });
  }

  addClaim(params: any): Observable<any> {
    return this.http.post(this.url + 'V1/claims', params);
  }

  getClaim(idOrUuid: string | number): Observable<any> {
    return this.http.get(this.url + 'V1/claims/' + idOrUuid);
  }

  updateClaim(uuid: string, params: any): Observable<any> {
    return this.http.put(this.url + 'V1/claims/' + uuid, params);
  }

  submitClaim(uuid: string): Observable<any> {
    return this.http.post(this.url + 'V1/claims/' + uuid + '/submit', {});
  }

  deleteRequest(uuid: string): Observable<any> {
    return this.http.delete(this.url + 'V1/requests/' + uuid);
  }

  /** Soft-discard del borrador (DISCARDED). Hard-delete queda para soporte. */
  discardRequest(uuid: string): Observable<any> {
    return this.http.post(this.url + 'V1/requests/' + uuid + '/discard', {});
  }

  uploadClaimDocument(uuid: string, file: File, extras?: { expirationDate?: string | null }): Observable<any> {
    const fd = new FormData();
    fd.append('file', file);
    if (extras?.expirationDate) {
      fd.append('expirationDate', extras.expirationDate);
    }
    return this.http.put(`${this.url}V1/documents/upload/${uuid}?entityType=CLAIM`, fd);
  }

  getDeductiblePercentages(vehicleUuid: string, partyUuid: string, date: string): Observable<any> {
    return this.http.get(`${this.url}V1/claims/vehicle/${vehicleUuid}/deductible-percentages`, {
      params: { partyUuid, date }
    });
  }

  createDocumentClaim(payload: any): Observable<any> {
    return this.http.post(`${this.url}V1/document-claim`, payload);
  }

  deleteDocumentClaim(uuid: string): Observable<any> {
    return this.http.delete(`${this.url}V1/document-claim/${uuid}`);
  }

  createClaimDetail(payload: any): Observable<any> {
    return this.http.post(`${this.url}V1/claim-detail`, payload);
  }

  updateClaimDetail(detailUuid: string, payload: any): Observable<any> {
    return this.http.put(`${this.url}V1/claim-detail/${detailUuid}`, payload);
  }

  deleteClaimDetail(detailUuid: string): Observable<any> {
    return this.http.delete(`${this.url}V1/claim-detail/${detailUuid}`);
  }

  protected setTotals(records: any[]): any {
    return records ? {
      "startBalanceSum": this.calculateSum(records, 'startBalance'),
      "monthlyContribution": this.calculateSum(records, 'monthlyContribution'),
      "endBalanceSum": this.calculateSum(records, 'endBalance'),
      "percentSum": this.calculateSum(records, 'percent'),
      "interestEarnedSum": this.calculateSum(records, 'interestEarned'),
      "totalSum": this.calculateSum(records, 'total')
    } : {
      "startBalanceSum": 0,
      "monthlyContribution": 0,
      "endBalanceSum": 0,
      "percentSum": 0,
      "interestEarnedSum": 0,
      "totalSum": 0
    };
  }

  protected calculateSum(filteredRecords: any[], field: string): number {
    return filteredRecords.reduce((acc, record) => {
      const value = parseFloat(record[field]);
      return acc + (isNaN(value) ? 0 : value);
    }, 0);
  }

  protected applyFilter(records: any[], params: any): any[] {
    const filterKeys = Object.keys(params.params);
    let result = [...records];
    filterKeys.forEach(key => {
      if (params.params[key]) {
        const searchTerm = params.params[key].toString().toLowerCase();
        result = result.filter(record =>
          record[key].toString().toLowerCase().includes(searchTerm)
        );
      }
    });
    return result;
  }
}
